<!-- 調査画面の共通処理はこのコンポーネントに記述する -->
<script lang="ts">
/* eslint-disable max-lines */
import { computed, defineComponent, onMounted, onUnmounted, ref, watch, nextTick } from 'vue'
import InspectionFooter from '@/components/inspection/InspectionFooter.vue'
import InspectionStepper from '@/components/inspection/common/InspectionStepper.vue'
import { ROUTER_PATHS } from '@/constants/router/paths'
import { useRoute, useRouter } from 'vue-router'
import photoDBOperator from '@/accessors/indexedDB/photoDBOperator'
import { useInspectionResultStore } from '@/stores/inspectionResultUpsertStore'
import { useDisasterStore } from '@/stores/disasterStore'
import { useLoadingView } from '@/modules/miiaFw/composables/useLoadingView'
import { inspectionPages } from '@/constants/ui/inspection/common/pagesInfo'
import { useCommonModalStore } from '@/stores/commonModalStore'
import PhotoDBOperator from '@/accessors/indexedDB/photoDBOperator'
import { MODAL_RESULT } from '@/modules/miiaFw/types/components/commonModal'
import { NavigationDirection } from '@/constants/ui/inspection/common/operations'
import type { ExteriorRiskType } from '@/types/accessor/api/emergencyInspectionResult/assessmentDetails/step1'
import { EXTERIOR_RISK_TYPE } from '@/constants/accessor/api/emergencyInspectionResult/assessmentDetails/step1/exteriorRiskType'
import {
  inspectionValidation,
  setOverallResult,
  step2SkipConfirmDialog
} from '@/composables/inspection/inspectionParent'
import { setInspectorList } from '@/accessors/sessionStorage/inspectorList'
import { inspectionStepNameList } from '@/constants/ui/inspection/common/inspectionStepNameList'
import { useRouterUtils } from '@/router/useRouterUtils'

const defaultCurrentPath = ROUTER_PATHS.inspection.root + '/' + ROUTER_PATHS.inspection.basicInfo
const currentPath = ref<string>(defaultCurrentPath)

export default defineComponent({
  name: 'InspectionParent',
  components: {
    InspectionFooter,
    InspectionStepper
  },
  props: {},
  setup() {
    const route = useRoute()
    const disasterStore = useDisasterStore()
    const inspectionResultStore = useInspectionResultStore()
    const commonModalStore = useCommonModalStore()
    const { setLoadingView } = useLoadingView()
    /** routerインスタンス */
    const router = useRouter()
    const { transition } = useRouterUtils(router)
    const routerUtils = useRouterUtils(router)

    /** 次のページ遷移方向を示すナビゲーションフラグ */
    let nextNavigationDirection: NavigationDirection = NavigationDirection.STAY

    /** 未入力リスト */
    const unfilledList = ref<string[]>([])

    /** バリデーション実行フラグ */
    const validateFlag = ref<boolean>(false)

    /** バリデーション実行結果（true: 成功, false: エラー） */
    const validationResult = ref<boolean>(false)

    /** storeへの保存処理実行フラグ */
    const saveToStoreFlag = ref<boolean>(false)

    /** step1 一見して危険と判断される 入力内容 */
    const selectedExteriorRiskType = ref<ExteriorRiskType>(EXTERIOR_RISK_TYPE.UNSELECTED)

    /** 現在のページのインデックス（範囲は調査画面内に限定） */
    const currentPageIndex = computed(() => {
      return inspectionPages.findIndex(
        inspectionPages => inspectionPages.path === currentPath.value
      )
    })

    /**
     * 未入力項目リストを更新する
     * @param newUnfilledList 未入力項目リスト
     */
    const updateUnfilledList = (newUnfilledList: string[]): void => {
      unfilledList.value = newUnfilledList
    }

    /**
     * 前ページ遷移のトリガ
     * @param なし
     * @returns なし
     */
    const triggerPrevious = (): void => {
      if (currentPageIndex.value === 0) {
        commonModalStore
          .openConfirmModal({
            title: '調査ホーム画面に戻る',
            body: '入力内容が破棄されますがよろしいですか？',
            okTitle: 'はい',
            cancelTitle: 'いいえ'
          })
          .then(async result => {
            if (result === MODAL_RESULT.OK) {
              // indexedDBの写真データを削除（論理削除）
              const imageKeys = await PhotoDBOperator.queryPrefixMatchItems(
                [
                  disasterStore.govCode!,
                  disasterStore.disasterId!,
                  inspectionResultStore.commonResultSummary.assessmentId
                ].join('/')
              )

              // 最後のAPIから取得できた撮影画像は編集中データではないため削除対象外とする
              const excludeKeys = inspectionResultStore.lastFetchedImageData.flatMap(
                inspectionImageData =>
                  inspectionImageData.imageFilePathList.map(
                    filePath => inspectionImageData.imageDirectory + filePath
                  )
              )

              await Promise.all(
                imageKeys
                  .filter(
                    e => !excludeKeys.some(excludeKey => excludeKey.startsWith(e?.fileName ?? ''))
                  )
                  .map(async e => {
                    if (e?.fileName === undefined) return
                    await PhotoDBOperator.updateStateToDelete(e.fileName)
                  })
              )

              // 調査実施結果Storeのリセットは遷移先のonMount処理で実行されるため、ここでは実行しない
              transition(ROUTER_PATHS.home)
            }
          })
          .catch(async result => {
            if (result === MODAL_RESULT.CANCEL) {
              return
            }
          })
      } else {
        // ナビゲーションフラグを前ページにした上でStore保存処理実行
        nextNavigationDirection = NavigationDirection.PREVIOUS
        saveToStoreFlag.value = true
      }
    }

    /**
     * 前のページに遷移する
     * @param なし
     * @returns なし
     * @note 前のページが存在しない場合は、ホーム画面に遷移
     */
    const moveToPreviousPage = (): void => {
      // ナビゲーションフラグを元に戻す
      nextNavigationDirection = NavigationDirection.STAY
      transition(inspectionPages[currentPageIndex.value - 1].path)
    }

    /**
     * バリデーション結果受け取り
     * @param result バリデーション結果
     * @returns なし
     */
    const onValidationResult = (result: boolean): void => {
      validationResult.value = result
      validateFlag.value = false
    }

    /**
     * 次ページ遷移のトリガ
     * @param なし
     * @returns なし
     */
    const triggerNext = (): void => {
      // ナビゲーションフラグを前ページにした上でバリデーション処理実行
      nextNavigationDirection = NavigationDirection.NEXT
      validateFlag.value = true
    }

    /**
     * 次のページに遷移する
     * @param なし
     * @returns なし
     * @note 次のページがない場合は、ホーム画面に遷移
     */
    const moveToNextPage = async (): Promise<void> => {
      // ナビゲーションフラグを元に戻す
      nextNavigationDirection = NavigationDirection.STAY
      if (currentPageIndex.value < inspectionPages.length - 1) {
        // 判定結果の画面に遷移する場合遷移前に判定結果算出ロジックを実行し、ストアを更新する
        if (
          inspectionPages[currentPageIndex.value + 1].path ===
          ROUTER_PATHS.inspection.root + '/' + ROUTER_PATHS.inspection.result
        ) {
          setOverallResult()
        }
        transition(inspectionPages[currentPageIndex.value + 1].path)
      } else {
        try {
          setLoadingView(true)
          await completeInspection()
          transition(ROUTER_PATHS.home)
        } finally {
          setLoadingView(false)
        }
      }
    }

    /**
     * 調査を完了する
     */
    const completeInspection = async () => {
      // 調査実施結果をアップロードする
      await inspectionResultStore.apiUpdateInspectionResult()

      // 画像データをS3にアップロードする
      if (
        disasterStore.disaster?.govCode !== undefined &&
        disasterStore.disaster?.disasterId !== undefined &&
        inspectionResultStore.commonResultSummary.assessmentId !== undefined
      ) {
        await photoDBOperator
          .executeS3Upload(
            disasterStore.disaster.govCode,
            disasterStore.disaster.disasterId,
            inspectionResultStore.commonResultSummary.assessmentId
          )
          .catch(e => {
            throw e
          })
      }
      setInspectorList()
    }

    /**
     * 調査結果Storeへの保存完了時処理
     * @param なし
     * @returns なし
     */
    const savedInspectionResultStore = async (): Promise<void> => {
      saveToStoreFlag.value = false
      // すべてのstore保存処理が完了した後にページ遷移を実行するため、nextTickで待機
      await nextTick()
      if (nextNavigationDirection === NavigationDirection.NEXT) {
        if (
          inspectionPages[currentPageIndex.value + 1]?.path &&
          inspectionPages[currentPageIndex.value + 1].path ===
            ROUTER_PATHS.inspection.root + '/' + ROUTER_PATHS.inspection.step2 &&
          selectedExteriorRiskType.value !== EXTERIOR_RISK_TYPE.NOT_APPLICABLE
        ) {
          step2SkipConfirmDialog({ currentPageIndex: currentPageIndex, utils: routerUtils })
          setOverallResult()
          return
        }
        await moveToNextPage()
      } else if (nextNavigationDirection === NavigationDirection.PREVIOUS) {
        moveToPreviousPage()
      }
    }

    watch(
      () => route.path,
      newPath => {
        currentPath.value = newPath
        // 未入力リストを初期化
        updateUnfilledList([])
      }
    )

    /** バリデーション composable */
    inspectionValidation({
      validateFlag,
      unfilledList,
      validationResult,
      saveToStoreFlag
    })

    /** 一見して危険と判断される セレクト内容更新 */
    const onUpdateSelectedExteriorRiskType = (value: ExteriorRiskType) => {
      selectedExteriorRiskType.value = value
    }

    onMounted(() => {
      transition(currentPath.value)
    })

    onUnmounted(() => {
      // カレントパスを初期化しておく
      currentPath.value = defaultCurrentPath
    })

    // 定義された関数やデータを返却
    return {
      currentPath,
      validateFlag,
      saveToStoreFlag,
      inspectionStepNameList,
      currentPageIndex,
      updateUnfilledList,
      triggerPrevious,
      triggerNext,
      onValidationResult,
      savedInspectionResultStore,
      onUpdateSelectedExteriorRiskType
    }
  }
})
</script>

<template>
  <div class="sticky-top bg-white shadow py-4">
    <InspectionStepper
      :stepNameList="inspectionStepNameList"
      :currentPageIndex="currentPageIndex"
      class="d-flex justify-content-center"
    />
  </div>
  <RouterView
    :triggerValidate="validateFlag"
    :saveToStoreFlag="saveToStoreFlag"
    @updateUnfilledList="updateUnfilledList"
    @onValidationResult="onValidationResult"
    @savedInspectionResultStore="savedInspectionResultStore"
    class="inspection-footer-offset-space"
    @updateSelectedExteriorRiskType="onUpdateSelectedExteriorRiskType"
  />
  <InspectionFooter
    :currentPath="currentPath"
    @triggerPrevious="triggerPrevious"
    @triggerNext="triggerNext"
  />
</template>

<style scoped>
.inspection-footer-offset-space {
  padding-bottom: 105px;
}
</style>
